/**
 * Apple-Style Documentation Page with Floating Section Labels
 * No traditional sidebar - uses glassmorphic floating section indicators
 */

import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { documentationPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'
import {
  GettingStartedSection,
  QuickStartSection,
  ServicesSection,
  ApiReferenceSection,
  AuthenticationSection,
  IntegrationSection,
  CustomizationSection,
  DeploymentSection,
  TroubleshootingSection,
  SupportSection
} from './sections/DocumentationSections'

export const DocumentationPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Scroll hook available if needed for future enhancements
  useOptimizedScroll()

  // Define sections for Apple-style floating labels
  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      children: <GettingStartedSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'how-it-works',
      title: 'How It Works',
      children: <QuickStartSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'who-its-for',
      title: 'Who It\'s For',
      children: <ServicesSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'api-reference',
      title: 'API Reference',
      children: <ApiReferenceSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'authentication',
      title: 'Authentication',
      children: <AuthenticationSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'integration',
      title: 'Integration',
      children: <IntegrationSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'customization',
      title: 'Customization',
      children: <CustomizationSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'deployment',
      title: 'Deployment',
      children: <DeploymentSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      children: <TroubleshootingSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'support',
      title: 'Support',
      children: <SupportSection prefersReducedMotion={prefersReducedMotion.current} />
    }
  ]

  return (
    <>
      {/* SEO Head */}
      <SEOHead
        customTitle="Documentation - Mernaki | Complete Integration Guide"
        customDescription="Everything you need to integrate, deploy, and customize Mernaki services. Complete documentation for developers and IT teams."
        customMetadata={{
          structuredData: getStructuredData('docs')
        }}
      />

      {/* Navbar is now global - no individual page navbars needed */}

      {/* Main Content Wrapper with Apple-Style Design */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }} transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.15, 1], x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Apple-Style Content */}
        <div className="relative mx-auto pb-16" style={{
          maxWidth: '1200px',
          marginTop: '-20vh', // Reduced from -35vh to give more breathing room
          paddingLeft: '4rem',
          paddingRight: '4rem'
        }}>
          {/* Temporarily commented out - Sticky Section Labels with Scroll-Activation */}
          {/* <SectionObserver sections={sections} /> */}

          {/* Manual section rendering for now */}
          {sections.map((section, index) => (
            <div
              key={section.id}
              data-section-id={section.id}
              className="section-content"
              style={{
                marginBottom: index === 0 ? '4rem' : '6rem',
                minHeight: index === 0 ? '60vh' : '50vh',
                scrollMarginTop: '120px'
              }}
            >
              {section.children}
            </div>
          ))}
        </div>
      </div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.2, margin: "0px 0px -100px 0px" })

  return (
    <section ref={ref} className="relative flex items-center justify-center" style={{ backgroundColor: 'transparent', minHeight: '100vh' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.03em'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Documentation
        </motion.h1>
        <motion.p
          className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8"
          style={{
            color: '#666666',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 500
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Everything you need to integrate, deploy, and customize Mernaki services.
        </motion.p>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  if (isMobile) {
    return <PageMobileFooter {...documentationPageFooter} />
  }

  return (
    <footer
      className="relative"
      style={{
        backgroundColor: '#0E0E0E',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        paddingTop: '140px', // Extended padding to create background behind rounded corners
        paddingBottom: '40px',
        marginTop: '-80px' // Pull footer up to extend background behind page content
      }}
    >
      {/* Floating Gradient Divider */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Columns */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Column 1 - Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                alt="Mernaki Logo"
                className="object-contain"
                style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                loading="lazy"
              />
              <h3
                className="text-lg"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                  fontWeight: 400
                }}
              >
                Mernaki
              </h3>
            </div>
            <p
              className="text-sm leading-relaxed"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
            >
              Premium job pages that help companies attract better talent through modern design.
            </p>
          </div>

          {/* Column 2 - Documentation */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Documentation
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Getting Started', to: '#getting-started' },
                { label: 'How It Works', to: '#how-it-works' },
                { label: 'API Reference', to: '/developer/api' },
                { label: 'Authentication', to: '#authentication' }
              ].map((item, i) => (
                <li key={i}>
                  {item.to.startsWith('#') ? (
                    <a
                      href={item.to}
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.to}
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Support */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Support
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Help Center', to: '/help' },
                { label: 'Contact', to: '/contact' },
                { label: 'Status', to: '/status' },
                { label: 'Report Issue', to: '/report-issue' }
              ].map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Company */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Company
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'About', to: '/about' },
                { label: 'Team', to: '/team' },
                { label: 'Partnerships', to: '/partnerships' },
                { label: 'Privacy Policy', to: '/privacy-policy' },
                { label: 'Impressum', to: '/impressum' }
              ].map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="pt-8 text-center" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 300
            }}
          >
            © {new Date().getFullYear()} Mernaki. Crafted in Germany.
          </p>
        </div>
      </div>
    </footer>
  )
}